import type { CreateReactPlayerConfigInput, ReactPlayerInstance } from './types';
import { VideoPlayerProvider as Provider, VideoPreviewClassNames } from './types';
import { VIDEO_PLAYER_CONSTANTS } from './constants';
import { isLocalEnvironment, isProdEnvironment } from 'utils/environment';
import { datadogRum } from '@datadog/browser-rum';
import { cn } from 'utils/styles';

const YOUTUBE_IFRAME_API_URL = 'https://www.youtube.com/iframe_api';
const PRODUCTION_ORIGIN = 'https://www.chime.com';

let youTubeApiPreloaded = false;

/**
 * Pre-loads the YouTube IFrame API script with `data-ot-ignore` so that
 * OneTrust's auto-blocking (category C0004) does not rewrite the tag to
 * `type="text/plain"`.  Must run *before* `youtube-video-element` calls its
 * internal `loadScript`, otherwise OneTrust intercepts the tag first.
 *
 * OneTrust uses a Proxy on the script element's `src` setter that fires
 * synchronously when `script.src = url` is assigned. The Proxy checks for
 * `data-ot-ignore` at that moment, so the attribute MUST be set before `src`.
 *
 * If OneTrust already blocked an existing script tag (type="text/plain"),
 * we remove it and inject a fresh one with the ignore attribute.
 *
 * Safe to call multiple times — the script is only injected once.
 */
export const ensureYouTubeApi = () => {
  if (typeof document === 'undefined' || youTubeApiPreloaded) return;
  youTubeApiPreloaded = true;

  const existing = document.querySelector<HTMLScriptElement>(`script[src="${YOUTUBE_IFRAME_API_URL}"]`);
  if (existing) {
    if (existing.type !== 'text/plain') return;
    existing.remove();
  }

  const script = document.createElement('script');
  script.setAttribute('data-ot-ignore', '');
  script.async = true;
  script.src = YOUTUBE_IFRAME_API_URL;
  document.head.appendChild(script);
};

/**
 * Checks if a URL is a YouTube URL.
 * @param url - The URL to check.
 * @returns True if the URL is a YouTube URL, false otherwise.
 */
export const isYouTubeUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return (
      hostname === 'youtube.com' ||
      hostname === 'www.youtube.com' ||
      hostname === 'youtu.be' ||
      hostname === 'www.youtu.be'
    );
  } catch {
    // If URL parsing fails (e.g., relative URLs), it's not a YouTube URL
    return false;
  }
};

/**
 * Checks if a URL is a TikTok URL.
 * @param url - The URL to check.
 * @returns True if the URL is a TikTok URL, false otherwise.
 */
export const isTikTokUrl = (url: string) => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return hostname === 'tiktok.com' || hostname === 'www.tiktok.com' || hostname === 'vm.tiktok.com';
  } catch {
    // If URL parsing fails (e.g., relative URLs), it's not a TikTok URL
    return false;
  }
};

/**
 * Checks if a URL is an HTML5 URL.
 * @param url - The URL to check.
 * @returns True if the URL is an HTML5 URL, false otherwise.
 */
export const isHTMl5Url = (url: string) => {
  return url.startsWith('/') || url.startsWith('http') || url.startsWith('https');
};

/**
 * Determines the video player provider based on the URL
 * @param url - The video URL
 * @returns The video player provider
 */
export const getPlayerProvider = (url: string): Provider => {
  if (!url) {
    return Provider.Unknown;
  }

  try {
    if (isYouTubeUrl(url)) {
      return Provider.YouTube;
    }

    if (isTikTokUrl(url)) {
      return Provider.TikTok;
    }

    if (isHTMl5Url(url)) {
      return Provider.HTML5;
    }

    return Provider.Unknown;
  } catch (error) {
    // If URL parsing fails, fall back to basic checks
    // Only check for relative URLs as a safe fallback
    const errorMessage = `Failed to parse URL in getPlayerProvider: ${url} - ${error instanceof Error ? error.message : String(error)}`;
    datadogRum.addError(errorMessage);
    if (url.startsWith('/')) {
      return Provider.HTML5;
    }
    return Provider.Unknown;
  }
};

export const getYouTubeOrigin = () => {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }

  if (isProdEnvironment()) {
    return PRODUCTION_ORIGIN;
  }

  if (process.env.NEXT_PUBLIC_DEV_ORIGIN) {
    return process.env.NEXT_PUBLIC_DEV_ORIGIN;
  }

  if (process.env.PORT) {
    return `http://localhost:${process.env.PORT}`;
  }

  return 'http://localhost:3000';
};

/**
 * Creates ReactPlayer configuration object
 * @param config - Configuration parameters
 * @returns ReactPlayer props object
 */
export const createReactPlayerConfig = ({
  src,
  playing,
  loop,
  muted,
  controls,
  className,
  testId,
  onLoadStart,
  onPlay,
  onPause,
  onEnded,
  ref,
}: CreateReactPlayerConfigInput) => {
  const baseConfig = {
    src,
    playing,
    loop,
    muted,
    controls,
    width: VIDEO_PLAYER_CONSTANTS.REACT_PLAYER_WIDTH,
    height: VIDEO_PLAYER_CONSTANTS.REACT_PLAYER_HEIGHT,
    className,
    'data-testid': testId,
    onLoadStart,
    onPlay,
    onPause,
    onEnded,
  };

  if (isYouTubeUrl(src)) {
    return {
      ...baseConfig,
      config: {
        youtube: {
          origin: getYouTubeOrigin(),
          rel: 0,
          iv_load_policy: 3,
          enablejsapi: 1,
          fs: 1,
          disablekb: 1,
        },
      },
    };
  }

  // Add TikTok-specific configuration to handle volume control issues
  if (isTikTokUrl(src)) {
    return {
      ...baseConfig,
      ref: (playerRef: ReactPlayerInstance | null) => {
        // First, call the original ref function if it exists
        if (ref && typeof ref === 'function') {
          ref(playerRef);
        }

        if (playerRef && typeof playerRef === 'object') {
          // Override the ref to prevent volume setting on TikTok
          // tiktok-react-player will issue a console warning about volume control
          // if the volume is set. React Player sets the volume to 1 by default,
          // so we have to override the volume setter to prevent the warning.
          // https://github.com/cookpete/react-player/blob/26001ec8c32d91cededfcaed026955fc4de2157b/src/Player.tsx#L31
          // https://github.com/muxinc/media-elements/blob/2f5655c2bf415132e180d7c05d0f9c0a431072ae/packages/tiktok-video-element/tiktok-video-element.js#L247
          Object.defineProperty(playerRef, 'volume', {
            set: () => {
              if (isLocalEnvironment()) {
                // eslint-disable-next-line no-console
                console.debug('TikTok player volume setting ignored: TikTok does not support volume control');
              }
            },
            get: () => 1, // Return default volume
            configurable: true,
          });
        }
      },
    };
  }

  return baseConfig;
};

/**
 * Merges default and custom class names for the video player
 * @param classNames - Custom class names
 * @param rounded - Whether to add rounded classes
 * @returns Merged class names object
 */
export const getMergedClassNames = (
  classNames: { wrapper?: string; player?: string } = {},
  rounded: boolean = false,
) => {
  const mergedClassNames = {
    wrapper: `${VIDEO_PLAYER_CONSTANTS.DEFAULT_WRAPPER_CLASSES} ${classNames.wrapper || ''}`,
    player: `${VIDEO_PLAYER_CONSTANTS.DEFAULT_PLAYER_CLASSES} ${classNames.player || ''}`,
  };

  // Add rounded classes if specified
  if (rounded) {
    mergedClassNames.wrapper += ` ${VIDEO_PLAYER_CONSTANTS.ROUNDED_WRAPPER_CLASSES}`;
  }

  return mergedClassNames;
};

/**
 * Calculates the effective muted state for autoplay compliance
 * @param muted - User-specified muted state
 * @param autoPlay - Whether autoplay is enabled
 * @returns The effective muted state
 */
export const getEffectiveMuted = (muted: boolean, autoPlay: boolean): boolean => {
  return muted || autoPlay;
};

/**
 * Merges default and custom class names for the video preview
 * @param classNames - Custom class names
 * @returns Merged class names object
 */
export const getMergedPreviewClassNames = (classNames: Partial<VideoPreviewClassNames> = {}) => {
  const defaultClassNames: VideoPreviewClassNames = VIDEO_PLAYER_CONSTANTS.PREVIEW_DEFAULT_CLASSES;

  return {
    wrapper: cn(defaultClassNames.wrapper, classNames.wrapper),
    blur: cn(defaultClassNames.blur, classNames.blur),
    image: cn(defaultClassNames.image, classNames.image),
    portraitOverlay: cn(defaultClassNames.portraitOverlay, classNames.portraitOverlay),
    overlay: cn(defaultClassNames.overlay, classNames.overlay),
    label: cn(defaultClassNames.label, classNames.label),
  };
};

/**
 * Safely cleans up YouTube iframe to prevent orphaned iframe errors
 * @param playerRef - ReactPlayer reference
 */
export const cleanupYouTubeIframe = (playerRef: ReactPlayerInstance | null) => {
  if (!playerRef) return;

  try {
    const internalPlayer = typeof playerRef.getInternalPlayer === 'function' ? playerRef.getInternalPlayer() : null;

    if (internalPlayer && 'contentWindow' in internalPlayer && internalPlayer.contentWindow) {
      // Send stop command to YouTube iframe
      internalPlayer.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');

      // Remove the iframe from DOM to prevent orphaned iframe errors
      if (internalPlayer.parentNode) {
        internalPlayer.parentNode.removeChild(internalPlayer);
      }
    }
  } catch (error) {
    if (!isProdEnvironment()) {
      console.warn('Error cleaning up YouTube iframe:', error);
    }
  }
};

/**
 * Safely cleans up TikTok iframe to prevent orphaned iframe errors
 * @param playerRef - ReactPlayer reference
 */
export const cleanupTikTokIframe = (playerRef: ReactPlayerInstance | null) => {
  if (!playerRef) return;

  try {
    const internalPlayer = typeof playerRef.getInternalPlayer === 'function' ? playerRef.getInternalPlayer() : null;

    if (internalPlayer && 'contentWindow' in internalPlayer && internalPlayer.contentWindow) {
      // Send stop command to TikTok iframe
      internalPlayer.contentWindow.postMessage('{"event":"command","func":"stop","args":""}', '*');

      // Remove the iframe from DOM to prevent orphaned iframe errors
      if (internalPlayer.parentNode) {
        internalPlayer.parentNode.removeChild(internalPlayer);
      }
    }
  } catch (error) {
    if (!isProdEnvironment()) {
      console.warn('Error cleaning up TikTok iframe:', error);
    }
  }
};

/**
 * Creates stable video player props for testing and development
 * @param props - Video player props
 * @returns Stable video player props
 */
export const createStableVideoPlayerProps = ({
  video,
  classNames = {},
  autoPlay = false,
  muted = false,
  rounded = false,
  loop = false,
  hideNativeControls = false,
  playInBackground = false,
  testId = VIDEO_PLAYER_CONSTANTS.DEFAULT_TEST_ID,
  onReady = () => {},
  onStart = () => {},
  onPlay = () => {},
  onPause = () => {},
  onEnded = () => {},
  children,
}: {
  video: unknown;
  classNames?: { wrapper?: string; player?: string };
  autoPlay?: boolean;
  muted?: boolean;
  rounded?: boolean;
  loop?: boolean;
  hideNativeControls?: boolean;
  playInBackground?: boolean;
  testId?: string;
  onReady?: (currentPlayerId: string) => void;
  onStart?: (currentPlayerId: string) => void;
  onPlay?: (currentPlayerId: string) => void;
  onPause?: (currentPlayerId: string) => void;
  onEnded?: (currentPlayerId: string) => void;
  children?: React.ReactNode;
}) => ({
  video,
  classNames,
  autoPlay,
  muted,
  rounded,
  loop,
  hideNativeControls,
  playInBackground,
  testId,
  onReady,
  onStart,
  onPlay,
  onPause,
  onEnded,
  children,
});
